// O preço de um ciclo da assinatura: base + add-ons de perfil e de armazenamento.
//
// ⚠️ Esta regra existe DUAS vezes: aqui e copiada dentro de
// `supabase/functions/asaas-create-subscription/index.ts`, porque o Deno das edge functions não
// importa de fora da pasta da função e o deploy é achatado (mesmo caso do `validateCoupon`).
// Mudar uma sem a outra faz a tela mostrar um preço e a Asaas cobrar outro — a pior falha
// possível num checkout. Os testes deste arquivo valem para as duas cópias.

export type CicloDeCobranca = 'MONTHLY' | 'YEARLY';

export interface ComposicaoDoPreco {
  ciclo: CicloDeCobranca;
  /** Preço base mensal. Para quem já assinava, é o preço TRAVADO dele, não o de tabela. */
  mensal: number;
  /** Preço base anual. `null` quando o anual está desligado. */
  anual: number | null;
  perfisExtras: number;
  gbExtras: number;
  valorPorPerfil: number;
  valorPorGb: number;
}

/** Centavos, sempre — `0.1 + 0.2` em float não dá `0.3`, e isto vira valor de cobrança. */
const emCentavos = (v: number) => Math.round(v * 100);
const emReais = (centavos: number) => centavos / 100;

/**
 * O desconto do plano anual, deduzido dos próprios preços em vez de ser mais um campo.
 *
 * Se o anual custa R$463,20 e doze meses custam R$694,80, o fator é 0,6667 — e é ELE que se
 * aplica também aos add-ons. Sem isso, quem assina anual pagaria o plano com 33% de desconto e o
 * perfil adicional a preço cheio, o que não se explica em tela nenhuma.
 *
 * Devolve 1 quando não há anual configurado ou quando os números não permitem deduzir o fator.
 */
export function fatorDoAnual(mensal: number, anual: number | null): number {
  if (anual === null || mensal <= 0) return 1;
  const dozeMeses = emCentavos(mensal) * 12;
  if (dozeMeses <= 0) return 1;
  return emCentavos(anual) / dozeMeses;
}

/** Quanto a Asaas cobra em CADA cobrança deste ciclo. */
export function precoDaAssinatura(p: ComposicaoDoPreco): number {
  const addonsPorMes =
    emCentavos(p.valorPorPerfil) * Math.max(p.perfisExtras, 0) +
    emCentavos(p.valorPorGb) * Math.max(p.gbExtras, 0);

  if (p.ciclo === 'MONTHLY') {
    return emReais(emCentavos(p.mensal) + addonsPorMes);
  }

  // No anual a Asaas cobra uma vez por ano: o valor do ciclo é o ano inteiro.
  const base = p.anual !== null ? emCentavos(p.anual) : emCentavos(p.mensal) * 12;
  return emReais(Math.round(base + addonsPorMes * 12 * fatorDoAnual(p.mensal, p.anual)));
}

/** O mesmo preço, dividido por mês — é assim que a tela compara mensal e anual. */
export function precoMensalizado(p: ComposicaoDoPreco): number {
  const total = precoDaAssinatura(p);
  return p.ciclo === 'YEARLY' ? emReais(Math.round(emCentavos(total) / 12)) : total;
}
